import React, { useState, useEffect, useCallback } from 'react';
import { getRequestTypes, addRequestType, updateRequestType, deleteRequestType } from '../api';
import './RequestTypesPage.css';

function RequestTypesPage() {
  const [types, setTypes] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  
  // Выбранный тип для редактирования
  const [selectedType, setSelectedType] = useState(null);
  const [isNewType, setIsNewType] = useState(false);
  
  // Форма редактирования
  const [form, setForm] = useState({
    name: '',
    description: '',
    system_prompt: '',
    manual: '',
    output: '',
    estimated_time: 30,
    user_prompt: ''
  });
  const [formError, setFormError] = useState('');
  const [formSuccess, setFormSuccess] = useState('');

  // Модальное окно подтверждения удаления
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const fetchTypes = useCallback(async () => {
    setLoading(true);
    setError('');
    
    try {
      const result = await getRequestTypes();
      setTypes(result.types || {});
    } catch (err) {
      setError(err.message || 'Ошибка загрузки типов запросов');
      setTypes({});
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTypes();
  }, [fetchTypes]);

  // Выбор типа для редактирования
  const handleSelectType = (name) => {
    const typeData = types[name];
    if (!typeData) return;
    
    setSelectedType(name);
    setIsNewType(false);
    setForm({
      name: name,
      description: typeData.description || '',
      system_prompt: typeData.system_prompt || '',
      manual: typeData.manual || '',
      output: typeData.output || '',
      estimated_time: typeData.estimated_time ?? 30,
      user_prompt: typeData.user_prompt || ''
    });
    setFormError('');
    setFormSuccess('');
  };

  // Создание нового типа
  const handleNewType = () => {
    setSelectedType(null);
    setIsNewType(true);
    setForm({
      name: '',
      description: '',
      system_prompt: 'You are a helpful assistant. {Manual}\n\n{Output}\n\nProvide clear and accurate responses.',
      manual: '',
      output: '',
      estimated_time: 30,
      user_prompt: 'User data:\n{UserJson}\n\nUser request: {UserPrompt}'
    });
    setFormError('');
    setFormSuccess('');
  };

  // Отмена редактирования
  const handleCancel = () => {
    setSelectedType(null);
    setIsNewType(false);
    setForm({
      name: '',
      description: '',
      system_prompt: '',
      manual: '',
      output: '',
      estimated_time: 30,
      user_prompt: ''
    });
    setFormError('');
    setFormSuccess('');
  };

  // Изменение полей формы
  const handleFormChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
    setFormError('');
    setFormSuccess('');
  };

  // Сохранение
  const handleSave = async () => {
    setFormError('');
    setFormSuccess('');
    
    // Валидация
    if (isNewType && !form.name.trim()) {
      setFormError('Название типа обязательно');
      return;
    }
    
    if (!form.system_prompt.trim()) {
      setFormError('System prompt обязателен');
      return;
    }
    
    if (!form.user_prompt.trim()) {
      setFormError('User prompt обязателен');
      return;
    }
    
    const estimatedTime = parseInt(form.estimated_time, 10);
    if (isNaN(estimatedTime) || estimatedTime < 1) {
      setFormError('Время выполнения должно быть положительным числом');
      return;
    }
    
    setSaving(true);
    
    try {
      if (isNewType) {
        await addRequestType({
          name: form.name.trim(),
          description: form.description,
          system_prompt: form.system_prompt,
          manual: form.manual,
          output: form.output,
          estimated_time: estimatedTime,
          user_prompt: form.user_prompt
        });
        setFormSuccess(`Тип "${form.name}" успешно создан`);
        setIsNewType(false);
        setSelectedType(form.name.trim());
      } else {
        await updateRequestType(selectedType, {
          description: form.description,
          system_prompt: form.system_prompt,
          manual: form.manual,
          output: form.output,
          estimated_time: estimatedTime,
          user_prompt: form.user_prompt
        });
        setFormSuccess('Изменения сохранены');
      }
      
      await fetchTypes();
    } catch (err) {
      setFormError(err.message || 'Ошибка сохранения');
    } finally {
      setSaving(false);
    }
  };

  // Удаление
  const handleDelete = async () => {
    if (!deleteConfirm) return;
    
    setSaving(true);
    setFormError('');
    
    try {
      await deleteRequestType(deleteConfirm);
      setDeleteConfirm(null);
      handleCancel();
      await fetchTypes();
    } catch (err) {
      setFormError(err.message || 'Ошибка удаления');
    } finally {
      setSaving(false);
    }
  };

  const typeNames = Object.keys(types).sort();

  return (
    <div className="request-types-page">
      <div className="page-header">
        <h1>Типы запросов</h1>
        <div className="page-header-actions">
          <button className="primary" onClick={handleNewType}>
            + Добавить тип
          </button>
        </div>
      </div>

      {error && <div className="error-message">{error}</div>}

      {loading ? (
        <div className="loading">Загрузка типов запросов</div>
      ) : (
        <div className="types-layout">
          {/* Список типов */}
          <div className="types-list">
            <div className="types-list-header">Доступные типы ({typeNames.length})</div>
            
            {typeNames.length === 0 ? (
              <div className="types-empty">
                Типы запросов не настроены
              </div>
            ) : (
              <div className="types-items">
                {typeNames.map(name => (
                  <div
                    key={name}
                    className={`type-item ${selectedType === name ? 'active' : ''}`}
                    onClick={() => handleSelectType(name)}
                  >
                    <div className="type-item-name">{name}</div>
                    <div className="type-item-desc">
                      {types[name].description || 'Без описания'}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Форма редактирования */}
          <div className="type-editor">
            {!selectedType && !isNewType ? (
              <div className="editor-placeholder">
                <div className="editor-placeholder-icon">⚙️</div>
                <p>Выберите тип запроса для редактирования</p>
                <p className="text-muted">или создайте новый</p>
              </div>
            ) : (
              <div className="editor-form">
                <div className="editor-header">
                  <h2>{isNewType ? 'Новый тип запроса' : `Редактирование: ${selectedType}`}</h2>
                  {!isNewType && (
                    <button
                      className="danger"
                      onClick={() => setDeleteConfirm(selectedType)}
                      disabled={saving}
                    >
                      Удалить
                    </button>
                  )}
                </div>

                {formError && <div className="error-message">{formError}</div>}
                {formSuccess && <div className="success-message">{formSuccess}</div>}

                <div className="form-group">
                  <label>Название типа</label>
                  <input
                    type="text"
                    value={form.name}
                    onChange={(e) => handleFormChange('name', e.target.value)}
                    placeholder="например: music_analysis"
                    disabled={!isNewType}
                  />
                  {isNewType && (
                    <div className="field-hint">
                      Только латиница, цифры, _ и -. Начинается с буквы.
                    </div>
                  )}
                </div>

                <div className="form-group">
                  <label>Описание</label>
                  <input
                    type="text"
                    value={form.description}
                    onChange={(e) => handleFormChange('description', e.target.value)}
                    placeholder="Краткое описание типа запроса"
                  />
                </div>

                <div className="form-group">
                  <label>Время выполнения запроса (сек)</label>
                  <input
                    type="number"
                    min="1"
                    max="600"
                    value={form.estimated_time}
                    onChange={(e) => handleFormChange('estimated_time', e.target.value)}
                    placeholder="30"
                    style={{ maxWidth: '150px' }}
                  />
                  <div className="field-hint">
                    Оценка времени выполнения для прогресс-бара на клиенте. По умолчанию 30 секунд.
                  </div>
                </div>

                <div className="form-group">
                  <label>
                    System Prompt
                    <span className="label-badge">обязательно</span>
                  </label>
                  <textarea
                    value={form.system_prompt}
                    onChange={(e) => handleFormChange('system_prompt', e.target.value)}
                    placeholder="Системный промпт для AI модели..."
                    rows={6}
                  />
                  <div className="field-hint">
                    Используйте <code>{'{Manual}'}</code> для вставки текста из поля Manual,{' '}
                    <code>{'{Output}'}</code> — из поля Output.
                  </div>
                </div>

                <div className="form-group">
                  <label>Manual</label>
                  <textarea
                    value={form.manual}
                    onChange={(e) => handleFormChange('manual', e.target.value)}
                    placeholder="Дополнительные инструкции, подставляемые в {Manual}..."
                    rows={4}
                  />
                  <div className="field-hint">
                    Этот текст заменит <code>{'{Manual}'}</code> в System Prompt.
                  </div>
                </div>

                <div className="form-group">
                  <label>Output</label>
                  <textarea
                    value={form.output}
                    onChange={(e) => handleFormChange('output', e.target.value)}
                    placeholder="Описание выходных данных, подставляемое в {Output}..."
                    rows={4}
                  />
                  <div className="field-hint">
                    Этот текст заменит <code>{'{Output}'}</code> в System Prompt.
                  </div>
                </div>

                <div className="form-group">
                  <label>
                    User Prompt
                    <span className="label-badge">обязательно</span>
                  </label>
                  <textarea
                    value={form.user_prompt}
                    onChange={(e) => handleFormChange('user_prompt', e.target.value)}
                    placeholder="Шаблон пользовательского промпта..."
                    rows={6}
                  />
                  <div className="field-hint">
                    Плейсхолдеры: <code>{'{UserJson}'}</code> — данные из запроса,{' '}
                    <code>{'{UserPrompt}'}</code> — текст промпта пользователя.
                  </div>
                </div>

                <div className="editor-actions">
                  <button className="secondary" onClick={handleCancel} disabled={saving}>
                    Отмена
                  </button>
                  <button className="primary" onClick={handleSave} disabled={saving}>
                    {saving ? 'Сохранение...' : 'Сохранить'}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Модальное окно подтверждения удаления */}
      {deleteConfirm && (
        <div className="modal-overlay" onClick={() => setDeleteConfirm(null)}>
          <div className="modal-content modal-small" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Подтверждение удаления</h2>
              <button className="modal-close" onClick={() => setDeleteConfirm(null)}>×</button>
            </div>
            <div className="modal-body">
              <p>Вы уверены, что хотите удалить тип запроса <strong>{deleteConfirm}</strong>?</p>
              <p className="text-muted">Это действие нельзя отменить.</p>
            </div>
            <div className="modal-actions">
              <button className="secondary" onClick={() => setDeleteConfirm(null)} disabled={saving}>
                Отмена
              </button>
              <button className="danger" onClick={handleDelete} disabled={saving}>
                {saving ? 'Удаление...' : 'Удалить'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default RequestTypesPage;